import React from 'react';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { BatchSourceFilters } from '../../hooks/useBatchSource';
import { DEFAULT_BATCH_FILTERS } from '../../hooks/useBatchSource';
import { DiscoverPanel } from './DiscoverPanel';

interface FilterPanelProps {
  filters: BatchSourceFilters;
  setFilters: (updates: Partial<BatchSourceFilters>) => void;
  isLoading: boolean;
}

const TIER_OPTIONS = ['Tier1', 'Tier2', 'Tier3'];

const MARKET_STATUS_OPTIONS = [
  { value: 'any', label: 'All Statuses' },
  { value: 'on_market', label: 'On Market' },
  { value: 'off_market', label: 'Off Market' },
  { value: 'pending', label: 'Pending' },
  { value: 'sold', label: 'Sold' },
];

const PERMIT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'no_recent', label: 'No Recent Permits' },
  { value: 'has_permits', label: 'Has Permits' },
];

const VIOLATION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'has_violations', label: 'Has Violations' },
  { value: 'no_violations', label: 'No Violations' },
];

export const FilterPanel: React.FC<FilterPanelProps> = ({ filters, setFilters, isLoading }) => {
  const handleTierToggle = (tier: string) => {
    const updated = filters.tiers.includes(tier)
      ? filters.tiers.filter((t) => t !== tier)
      : [...filters.tiers, tier];
    setFilters({ tiers: updated, page: 1 });
  };

  const handleReset = () => {
    setFilters({ ...DEFAULT_BATCH_FILTERS });
  };

  const hasActiveFilters =
    filters.multifamilyOnly !== DEFAULT_BATCH_FILTERS.multifamilyOnly ||
    filters.unitMin !== null ||
    filters.unitMax !== null ||
    filters.permitFilter !== 'all' ||
    filters.violationFilter !== 'all' ||
    filters.tiers.length > 0 ||
    filters.zipCodes.length > 0 ||
    filters.marketStatus !== 'any';

  return (
    <div className="h-full overflow-y-auto p-4 space-y-6">
      {/* Discover New Deals */}
      <DiscoverPanel />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-gold" />
          <h2 className="text-lg font-semibold text-ivory">Filters</h2>
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-gold transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        )}
      </div>

      {/* Multi-family toggle */}
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            className={`relative w-10 h-5 rounded-full transition-colors ${
              filters.multifamilyOnly ? 'bg-gold' : 'bg-slate-600'
            }`}
            onClick={() => setFilters({ multifamilyOnly: !filters.multifamilyOnly, page: 1 })}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                filters.multifamilyOnly ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </div>
          <span className="text-sm text-ivory">Multi-family only</span>
        </label>
      </div>

      {/* Unit Range */}
      <div>
        <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
          Unit Count
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min"
            min={1}
            value={filters.unitMin ?? ''}
            onChange={(e) =>
              setFilters({ unitMin: e.target.value ? Number(e.target.value) : null, page: 1 })
            }
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
          />
          <span className="text-stone-500 text-sm">to</span>
          <input
            type="number"
            placeholder="Max"
            min={1}
            value={filters.unitMax ?? ''}
            onChange={(e) =>
              setFilters({ unitMax: e.target.value ? Number(e.target.value) : null, page: 1 })
            }
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
          />
        </div>
      </div>

      {/* Permit Filter */}
      <div>
        <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
          Permits
        </label>
        <div className="space-y-1.5">
          {PERMIT_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="permit"
                checked={filters.permitFilter === opt.value}
                onChange={() =>
                  setFilters({ permitFilter: opt.value as BatchSourceFilters['permitFilter'], page: 1 })
                }
                className="w-4 h-4 accent-amber-500"
              />
              <span className="text-sm text-stone-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Violation Filter */}
      <div>
        <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
          Violations
        </label>
        <div className="space-y-1.5">
          {VIOLATION_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="violation"
                checked={filters.violationFilter === opt.value}
                onChange={() =>
                  setFilters({ violationFilter: opt.value as BatchSourceFilters['violationFilter'], page: 1 })
                }
                className="w-4 h-4 accent-amber-500"
              />
              <span className="text-sm text-stone-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Neighborhood Tier */}
      <div>
        <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
          Neighborhood Tier
        </label>
        <div className="space-y-1.5">
          {TIER_OPTIONS.map((tier) => (
            <label key={tier} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.tiers.includes(tier)}
                onChange={() => handleTierToggle(tier)}
                className="w-4 h-4 accent-amber-500 rounded"
              />
              <span className="text-sm text-stone-300">{tier}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ZIP Codes */}
      <div>
        <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
          ZIP Codes
        </label>
        <input
          type="text"
          placeholder="e.g. 19140, 19141"
          value={filters.zipCodes.join(', ')}
          onChange={(e) => {
            const zips = e.target.value
              .split(',')
              .map((z) => z.trim())
              .filter(Boolean);
            setFilters({ zipCodes: zips, page: 1 });
          }}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
        />
        <p className="text-xs text-stone-500 mt-1">Comma-separated</p>
      </div>

      {/* Market Status */}
      <div>
        <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
          Market Status
        </label>
        <select
          value={filters.marketStatus}
          onChange={(e) => setFilters({ marketStatus: e.target.value, page: 1 })}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none"
        >
          {MARKET_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <div className="w-3 h-3 border border-gold border-t-transparent rounded-full animate-spin" />
          Filtering...
        </div>
      )}
    </div>
  );
};
